/**
 * Algolia Search Integration (Articles Only)
 * Powers both predictive search (header dropdown) and the /search results page.
 * Overrides the default Shopify search entirely when Algolia is configured.
 */
(function () {
  'use strict';

  // ==========================================
  // CONFIGURATION & INITIALIZATION
  // ==========================================

  if (!window.algoliaConfig) {
    console.warn('Algolia config not found');
    return;
  }

  const {
    appId,
    searchKey,
    indexName,
    hitsPerPage = 9,
    searchPageEnabled = true,
    searchPageResults = 24
  } = window.algoliaConfig;

  if (!appId || !searchKey || !indexName) {
    console.warn('Algolia credentials incomplete');
    return;
  }

  const SEARCH_ATTRIBUTES = [
    'objectID', 'title', 'url', 'handle',
    'image', 'featured_image', 'published_at',
    'created_at', 'author', 'blog_handle', 'blog'
  ];

  let algoliaIndex = null;

  function initAlgolia() {
    if (typeof algoliasearch === 'undefined') {
      setTimeout(initAlgolia, 100);
      return;
    }

    const client = algoliasearch(appId, searchKey);
    algoliaIndex = client.initIndex(indexName);

    overridePredictiveSearch();
    initSearchResultsPage();
  }

  // ==========================================
  // SHARED UTILITIES
  // ==========================================

  function highlightText(text, searchTerms) {
    if (!text || !searchTerms) return text || '';
    const terms = searchTerms.toLowerCase().split(/\s+/).filter(t => t.length > 0);
    let result = text;
    terms.forEach(term => {
      const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      result = result.replace(regex, '<mark>$1</mark>');
    });
    return result;
  }

  function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  function getArticleUrl(hit) {
    if (hit.url) return hit.url;
    const blogHandle = hit.blog_handle || (hit.blog && hit.blog.handle);
    if (blogHandle && hit.handle) return `/blogs/${blogHandle}/${hit.handle}`;
    return '#';
  }

  // ==========================================
  // ARTICLE CARD RENDERER
  // ==========================================

  function renderArticleCard(hit) {
    const imageUrl = hit.image || hit.featured_image || '';
    const title = hit.title || '';
    const url = getArticleUrl(hit);

    const authorName = hit.author
      ? (typeof hit.author === 'object' ? (hit.author.name || '') : hit.author)
      : 'duPont REGISTRY';
    let publishDate = '';
    if (hit.published_at || hit.created_at) {
      const dateObj = new Date(hit.published_at || hit.created_at);
      publishDate = dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }

    return `
      <article class="card card--article card--style-padding card--text-display--title">
        <a class="card__image" href="${url}">
          <figure class="aspect-ratio--landscape aspect-ratio__mobile--landscape">
            ${imageUrl ? `<img src="${imageUrl}" alt="${escapeHtml(title)}" class="img-absolute" loading="lazy" width="400" height="300">` : ''}
          </figure>
        </a>
        <a href="${url}" class="card__content">
          <div>
            ${authorName || publishDate ? `<p class="card__blog-title text-xs">by ${escapeHtml(authorName)}${publishDate ? ` – ${publishDate}` : ''}</p>` : ''}
            <p class="card__title">${escapeHtml(title)}</p>
          </div>
          <div class="card__bottom">
            <p class="button button--primary">${window.searchStrings?.read_more || 'Read More'}</p>
          </div>
        </a>
      </article>
    `;
  }

  // ==========================================
  // PREDICTIVE SEARCH — sidebar + grid renders
  // ==========================================

  function renderSidebar(hits, searchTerms) {
    let listHtml = '';

    if (hits.length > 0) {
      const itemsHtml = hits.map(hit => {
        const title = hit._highlightResult?.title?.value || highlightText(hit.title, searchTerms);
        return `<li class="searches-search__item"><a href="${getArticleUrl(hit)}">${title}</a></li>`;
      }).join('');
      listHtml = `
        <div class="search-sidebar__list">
          <p class="search-sidebar__header text-md">${window.searchStrings?.articles || 'Articles'}</p>
          <ul class="article-search">${itemsHtml}</ul>
        </div>
      `;
    } else {
      listHtml = `
        <div class="search-sidebar__list">
          <p class="search-sidebar__header text-md">${window.searchStrings?.title_no_results_article || 'No articles found'}</p>
        </div>
      `;
    }

    return `<div class="search-sidebar__results">${listHtml}</div>`;
  }

  function renderGrid(hits, searchTerms) {
    if (hits.length === 0) {
      return `
        <div class="no-results-notice">
          <p>${window.searchStrings?.no_results_predictive || 'No results found for your search.'}</p>
        </div>
      `;
    }

    const sorted = [...hits].sort((a, b) => {
      return new Date(b.published_at || 0) - new Date(a.published_at || 0);
    });

    const cardsHtml = sorted.map(hit => renderArticleCard(hit)).join('');

    let viewAllHtml = '';
    if (hits.length >= hitsPerPage) {
      const encodedTerms = encodeURIComponent(searchTerms);
      viewAllHtml = `
        <a href="/search?q=${encodedTerms}" class="button button--primary">
          ${window.searchStrings?.view_all || 'View all'}
        </a>
      `;
    }

    return `
      <div class="search-products__row">
        <div class="search-products__grid" tabindex="-1">
          ${cardsHtml}
          ${viewAllHtml}
        </div>
      </div>
    `;
  }

  // ==========================================
  // PREDICTIVE SEARCH OVERRIDE
  // ==========================================
  
  function overridePredictiveSearch() {
    if (!customElements.get('predictive-search')) {
      setTimeout(overridePredictiveSearch, 100);
      return;
    }

    document.querySelectorAll('predictive-search').forEach(element => {
      element.search = async function (event) {
        const query = event.target.value;

        if (!query || !algoliaIndex) return;

        const resultsContainer = this.querySelector('.predictive-search__results-container');
        if (!resultsContainer) return;

        resultsContainer.classList.add('loading');

        try {
          const { hits } = await algoliaIndex.search(query, {
            hitsPerPage: hitsPerPage,
            attributesToRetrieve: SEARCH_ATTRIBUTES,
            attributesToHighlight: ['title']
          });

          // Update sidebar
          const sidebar = resultsContainer.querySelector('.search-sidebar');
          if (sidebar) {
            let sidebarInner = sidebar.querySelector('#search-sidebar');
            if (!sidebarInner) {
              sidebarInner = document.createElement('div');
              sidebarInner.id = 'search-sidebar';
              sidebarInner.classList.add('search-sidebar__initial');
              sidebar.prepend(sidebarInner);
            }
            const existingResults = sidebar.querySelector('.search-sidebar__results');
            if (existingResults) existingResults.remove();
            sidebar.insertAdjacentHTML('beforeend', renderSidebar(hits, query));

            if (this.populateRecentSearches) {
              this.populateRecentSearches(resultsContainer);
            }
          }

          // Update grid
          const productsSection = resultsContainer.querySelector('.search-products');
          if (productsSection) {
            productsSection.innerHTML = renderGrid(hits, query);
          }

          // Update recent searches across all predictive-search elements
          const allPredictiveSearch = document.querySelectorAll('predictive-search');
          allPredictiveSearch.forEach(ps => {
            if (ps.recentSearches && !ps.recentSearches.includes(query)) {
              if (ps.recentSearches.length > 4) {
                ps.recentSearches.pop();
              }
              ps.recentSearches.unshift(query);
              sessionStorage.setItem('recentSearches', JSON.stringify(ps.recentSearches));
            }
            if (ps.addRecentSearchesListeners) {
              ps.addRecentSearchesListeners();
            }
          });

        } catch (error) {
          console.error('Algolia predictive search error:', error);
        } finally {
          resultsContainer.classList.remove('loading');
        }
      };
    });

    console.log('Algolia predictive search active');
  }

  // ==========================================
  // SEARCH RESULTS PAGE
  // ==========================================

  const RESULTS_PER_PAGE = searchPageResults;
  let currentPage = 0;
  let currentQuery = '';
  let totalPages = 0;

  function isSearchResultsPage() {
    return document.body.classList.contains('template-search') ||
      window.location.pathname === '/search';
  }

  function renderPagination(currentPage, totalPages) {
    if (totalPages <= 1) return '';

    if (!document.querySelector('link[href*="component-pagination.css"]')) {
      const cssLink = document.createElement('link');
      cssLink.rel = 'stylesheet';
      cssLink.href = window.Shopify?.routes?.root
        ? `${window.Shopify.routes.root}cdn/shop/t/1/assets/component-pagination.css`
        : '/cdn/shop/t/1/assets/component-pagination.css';
      document.head.appendChild(cssLink);
    }

    let html = '<nav class="pagination" role="navigation" aria-label="Pagination">';
    html += '<ul role="list">';

    if (currentPage > 0) {
      html += `
        <li class="prev">
          <a href="#" data-page="${currentPage - 1}" aria-label="Previous page">
            &larr; <p class="hide-mobile">Previous</p>
          </a>
        </li>
      `;
    }

    const maxVisible = 5;
    let startPage = Math.max(0, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(0, endPage - maxVisible + 1);
    }

    if (startPage > 0) {
      html += `<li><a href="#" data-page="0">1</a></li>`;
      if (startPage > 1) {
        html += '<li><span>...</span></li>';
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      if (i === currentPage) {
        html += `
          <li>
            <span class="pagination__item--current" aria-current="page">${i + 1}</span>
          </li>
        `;
      } else {
        html += `<li><a href="#" data-page="${i}">${i + 1}</a></li>`;
      }
    }

    if (endPage < totalPages - 1) {
      if (endPage < totalPages - 2) {
        html += '<li><span>...</span></li>';
      }
      html += `<li><a href="#" data-page="${totalPages - 1}">${totalPages}</a></li>`;
    }

    if (currentPage < totalPages - 1) {
      html += `
        <li class="next">
          <a href="#" data-page="${currentPage + 1}" aria-label="Next page">
            <p class="hide-mobile">Next</p> &rarr;
          </a>
        </li>
      `;
    }

    html += '</ul></nav>';
    return html;
  }

  function attachPaginationListeners() {
    document.querySelectorAll('.pagination a[data-page]').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const page = parseInt(link.dataset.page, 10);
        performSearchPageSearch(currentQuery, page);
      });
    });
  }

  async function performSearchPageSearch(query, page = 0) {
    if (!algoliaIndex || !query) return;

    currentQuery = query;
    currentPage = page;

    const gridContainer = document.getElementById('grid-product');
    const productGridContainer = document.getElementById('ProductGridContainer');

    if (!gridContainer) return;

    gridContainer.style.opacity = '0.3';
    gridContainer.style.pointerEvents = 'none';

    try {
      const { hits, nbHits, nbPages } = await algoliaIndex.search(query, {
        hitsPerPage: RESULTS_PER_PAGE,
        page: page,
        attributesToRetrieve: SEARCH_ATTRIBUTES,
        attributesToHighlight: ['title']
      });

      totalPages = nbPages;

      const heading = document.querySelector('.search-results h1');
      if (heading && nbHits > 0) {
        heading.textContent = window.searchStrings?.title_results || 'Search results';
      }

      if (hits.length === 0) {
        gridContainer.innerHTML = `
          <p class="grid-archive__no-results">
            ${window.searchStrings?.nothing_found || 'No results found'}
          </p>
        `;
      } else {
        let cardsHtml = hits.map(hit => renderArticleCard(hit)).join('');
        cardsHtml += renderPagination(currentPage, totalPages);
        gridContainer.innerHTML = cardsHtml;
        attachPaginationListeners();
      }

      const newUrl = new URL(window.location);
      newUrl.searchParams.set('q', query);
      if (page > 0) {
        newUrl.searchParams.set('page', page + 1);
      } else {
        newUrl.searchParams.delete('page');
      }
      window.history.pushState({}, '', newUrl);

      if (page > 0) {
        productGridContainer?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

    } catch (error) {
      console.error('Algolia search page error:', error);
    } finally {
      gridContainer.style.opacity = '1';
      gridContainer.style.pointerEvents = 'auto';
    }
  }

  function initSearchResultsPage() {
    if (!isSearchResultsPage() || !searchPageEnabled) return;

    // Clear the predictive search input in the header on search page load
    document.querySelectorAll('predictive-search').forEach(ps => {
      const input = ps.querySelector('input[type="search"], input[name="q"]');
      if (input) input.value = '';
    });

    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get('q');
    const page = parseInt(urlParams.get('page') || '1', 10) - 1;

    if (query) {
      const gridContainer = document.getElementById('grid-product');
      if (gridContainer) {
        gridContainer.style.opacity = '0';
        gridContainer.innerHTML = `
          <div style="display: flex; justify-content: center; align-items: center; min-height: 400px; width: 100%;">
            <div style="text-align: center;">
              <div style="width: 40px; height: 40px; border: 3px solid #f3f3f3; border-top: 3px solid #333; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto 1rem;"></div>
              <p style="color: #666;">Loading search results...</p>
            </div>
          </div>
          <style>
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          </style>
        `;
        setTimeout(() => { gridContainer.style.opacity = '1'; }, 10);
      }

      performSearchPageSearch(query, Math.max(0, page));
    }

    // Override form submission
    const searchForm = document.getElementById('FacetSearchForm');
    if (searchForm) {
      searchForm.addEventListener('submit', (e) => {
        e.preventDefault();
        const formData = new FormData(searchForm);
        const q = formData.get('q');
        if (q) performSearchPageSearch(q, 0);
      });

      const searchInput = searchForm.querySelector('input[name="q"]');
      if (searchInput) {
        let debounceTimer;
        searchInput.addEventListener('input', (e) => {
          clearTimeout(debounceTimer);
          const q = e.target.value.trim();
          if (q) {
            debounceTimer = setTimeout(() => {
              performSearchPageSearch(q, 0);
            }, 500);
          }
        });
      }
    }

    // Handle browser back/forward
    window.addEventListener('popstate', () => {
      const params = new URLSearchParams(window.location.search);
      const q = params.get('q');
      const p = parseInt(params.get('page') || '1', 10) - 1;
      if (q) performSearchPageSearch(q, Math.max(0, p));
    });
  }

  // ==========================================
  // BOOT
  // ==========================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAlgolia);
  } else {
    initAlgolia();
  }
})();
